<template>
  <!--
    Flat list of <li> (header / link / group) — keeps ALL Vuexy styling and
    behaviour (collapse, accordion, active state), which rely on items being
    direct `.navigation > li` children.

    Re-ordering: only the section title (`.navigation-header`) is a drag handle,
    so links keep navigating. Press & hold a title to drag it — on drop the whole
    section (title + its items, up to the next title) is moved as one block and
    the order is saved to localStorage (per browser / PC).
  -->
  <draggable
    tag="ul"
    v-model="model"
    handle=".navigation-header"
    :animation="200"
    :fallback-tolerance="4"
    ghost-class="nav-drag-ghost"
    chosen-class="nav-drag-chosen"
    @end="onDragEnd"
  >
    <component
      :is="resolveNavItemComponent(item)"
      v-for="item in model"
      :key="itemKey(item)"
      :item="item"
    />
  </draggable>
</template>

<script>
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { resolveVerticalNavMenuItemComponent as resolveNavItemComponent } from '@core/layouts/utils'
import VerticalNavMenuHeader from '../vertical-nav-menu-header/index'
import VerticalNavMenuLink from '../vertical-nav-menu-link/VerticalNavMenuLink.vue'
import VerticalNavMenuGroup from '../vertical-nav-menu-group/VerticalNavMenuGroup.vue'
import mixinRouter from '@/components/mixin/mixinRouter'

const ORDER_KEY = 'crm-menu-section-order'

export default {
  name: 'VerticalNavMenuItems',
  mixins: [mixinRouter],
  components: {
    draggable,
    VerticalNavMenuHeader,
    VerticalNavMenuLink,
    VerticalNavMenuGroup,
  },
  data: () => ({
    resolveNavItemComponent,
    model: [],        // flat list rendered (and mutated while dragging)
    sectionOrder: [], // persisted section-key order
  }),
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters('user', ['myRole', 'isAdminUser']),
    filteredItems() {
      if (this.isAdminUser) return this.items
      const items = this.items.filter((item) => this.routerIsAccessible(item.route))
      return items.filter((item, index) => {
        if (item.header != null) {
          return items[index + 1] && items[index + 1].header == null
        }
        if (item.children) {
          item.children = item.children.filter((child) => this.routerIsAccessible(child.route))
          return item.children.length > 0
        }
        return true
      })
    },
    // Group the flat list into [{ key, header, items }] sections.
    sections() {
      const out = []
      let current = null
      this.filteredItems.forEach((it) => {
        if (it.header != null) {
          current = { key: `h:${it.header}`, header: it.header, items: [it] }
          out.push(current)
        } else {
          if (!current) {
            current = { key: '__lead__', header: null, items: [] }
            out.push(current)
          }
          current.items.push(it)
        }
      })
      return out
    },
    // Sections ordered by the persisted order, flattened back to a flat list.
    // Lead (header-less) items stay on top; new sections keep natural position.
    orderedFlat() {
      const lead = this.sections.filter((s) => s.header == null)
      const rest = this.sections.filter((s) => s.header != null)
      const map = new Map(rest.map((s) => [s.key, s]))
      const ordered = [];
      (this.sectionOrder || []).forEach((k) => {
        if (map.has(k)) {
          ordered.push(map.get(k))
          map.delete(k)
        }
      })
      rest.forEach((s) => {
        if (map.has(s.key)) ordered.push(s)
      })
      return [...lead, ...ordered].reduce((acc, s) => acc.concat(s.items), [])
    },
  },
  watch: {
    orderedFlat: {
      immediate: true,
      handler(v) {
        this.model = v.slice()
      },
    },
  },
  created() {
    this.loadOrder()
  },
  methods: {
    itemKey(item) {
      return item.header ? `header-${item.header}` : `title-${item.title}`
    },
    loadOrder() {
      try {
        const raw = localStorage.getItem(ORDER_KEY)
        if (raw) this.sectionOrder = JSON.parse(raw) || []
      } catch (e) {
        this.sectionOrder = []
      }
    },
    persistOrder() {
      try {
        localStorage.setItem(ORDER_KEY, JSON.stringify(this.sectionOrder))
      } catch (e) {
        /* ignore */
      }
    },
    onDragEnd() {
      // Only headers are draggable, so their order in the (drag-mutated) model is
      // the new section order. Persist it and rebuild clean section blocks.
      this.sectionOrder = this.model
        .filter((i) => i.header != null)
        .map((i) => `h:${i.header}`)
      this.persistOrder()
      this.$nextTick(() => {
        this.model = this.orderedFlat.slice()
      })
    },
  },
}
</script>
